import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, HtmlBlock, PromisePool } from "bm-ui-kit";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import { InView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";
import type { WP_REST_API_Post, WP_REST_API_Posts, WP_REST_API_Tags } from "wp-types";

interface Props extends MeasureComponentProps {
	title: string;
	tags: string[];
	count: number;
}

interface State extends MeasureComponentState {
	loading: boolean;
	articles: WP_REST_API_Post[];
}

/**
 * @Shortcode
 *
 * @title "Kapcsolódó tartalmak"
 * @shortcode "related_news"
 * @type "widget"
 * @lazyHydrate "whenIdle"
 */
export default class RelatedNews extends MeasureComponent<Props, State> {
	protected productTypes = "";
	protected readonly componentType: EventComponentType = "widget";
	private readonly promisePool = new PromisePool();

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			articles: [],
			loading: true,
		};
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	private loadData(): void {
		const { tags, count } = this.props;

		this.promisePool
			.add(
				Promise.all(
					tags.map((tag) =>
						http
							.get(`/wp-json/wp/v2/tags?search=${tag}&orderby=count&order=desc&per_page=1`)
							.then((tagData: WP_REST_API_Tags) => tagData?.[0]?.id),
					),
				),
			)
			.then((tagIds: number[]) =>
				http.get(`/wp-json/wp/v2/posts/?_embed&per_page=${count}&tags=${tagIds.join(",")}`),
			)
			.then((articles: WP_REST_API_Posts) => {
				this.setState({
					articles,
					loading: false,
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	@autobind
	protected onVisibilityChange(isVisible: boolean): void {
		super.onVisibilityChange(isVisible);

		if (isVisible) {
			this.loadData();
		}
	}

	public render(): ReactNode {
		const { title, count } = this.props;
		const { wasVisible, articles, loading } = this.state;

		return (
			<InView
				as="aside"
				className="related-news widget"
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				<div className="headline">
					<h2 className="h6 text-uppercase">{title}</h2>
				</div>
				<Row>
					{loading
						? Array.from(Array(count).keys()).map((id) => this.renderSkeletonArticle(id))
						: articles?.map((article) => this.renderArticle(article)) || this.renderMissingArticles()}
				</Row>
			</InView>
		);
	}

	private renderArticle(article: WP_REST_API_Post): ReactNode {
		const { count } = this.props;

		const grid = 12 / count;

		return (
			<Col key={article.id} lg={grid} sm={grid * 2}>
				<article>
					<header>
						<a className="text-body" href={`${article.link}?link_type=article-offer-block`}>
							{article._embedded?.["wp:featuredmedia"]?.["0"] && (
								<figure className="text-center">
									<img
										src={(article._embedded["wp:featuredmedia"]["0"] as any).source_url}
										className="img-thumbnail related-content-image d-sm-block mb-2"
										alt={article.title.rendered}
										loading="lazy"
									/>
								</figure>
							)}
							<h3 className="h4" dangerouslySetInnerHTML={{ __html: article.title.rendered }} />
						</a>
					</header>
					<div className="text-muted small mb-2">
						<time dateTime={article.date}>{dayjs(article.date).format("YYYY. MM. DD.")}</time>
					</div>
					<HtmlBlock
						as="p"
						className="content"
						html={`${article.excerpt.rendered
							.replace(/<[^>]+>/g, "")
							.split(" ")
							.slice(0, 30)
							.join(" ")}...`}
					/>
				</article>
			</Col>
		);
	}

	private renderMissingArticles(): ReactNode {
		return (
			<Col>
				<article>
					<header className="text-danger">Sajnáljuk!</header>
					<div className="text-danger mb-3">Hiba lépett fel a tartalmak betöltése közben.</div>
				</article>
			</Col>
		);
	}

	private renderSkeletonArticle(id: number): ReactNode {
		const { count } = this.props;

		const grid = 12 / count;

		return (
			<Col key={id} lg={grid} sm={grid * 2}>
				<article>
					<header>
						<figure className="text-center">
							<Skeleton height={150} />
						</figure>
						<h3 className="h4">
							<Skeleton count={2} />
						</h3>
					</header>
					<div className="text-muted small mb-2">
						<time>
							<Skeleton count={1} />
						</time>
					</div>
					<p className="content">
						<Skeleton count={8} />
					</p>
				</article>
			</Col>
		);
	}

	protected getComponentName(): string {
		return "RelatedNews";
	}
}
